import { type MentionOptions } from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import {
    MentionSuggestionWrapper,
    type MentionSuggestionWrapperRef,
} from './components/MentionSuggestionWrapper';

/**
 * Workaround for the current typing incompatibility between Tippy.js and Tiptap
 * Suggestion utility, adapted for entity mentions.
 * @see https://github.com/ueberdosis/tiptap/issues/2795#issuecomment-1160623792
 */
const DOM_RECT_FALLBACK: DOMRect = {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
    x: 0,
    y: 0,
    toJSON() {
        return {};
    },
};

interface MentionSuggestionCallbacks {
    onStart?: () => void;
    onExit?: () => void;
    onSelect?: () => void;
}

export const generateEntityMentionSuggestion = (
    callbacks?: MentionSuggestionCallbacks,
): MentionOptions['suggestion'] => ({
    allowSpaces: true,
    /**
     * creates and manages the dropdown component
     */
    render: () => {
        let component: ReactRenderer<MentionSuggestionWrapperRef> | undefined;
        let popup: TippyInstance | undefined;

        return {
            /**
             * Called when mention suggestion starts
             */
            onStart: (props) => {
                callbacks?.onStart?.();

                component = new ReactRenderer(MentionSuggestionWrapper, {
                    props,
                    editor: props.editor,
                });

                popup = tippy('body', {
                    getReferenceClientRect: () =>
                        props.clientRect?.() ?? DOM_RECT_FALLBACK,
                    appendTo: 'parent',
                    content: component.element,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                    offset: [0, 4], // [skidding, distance] - reduces vertical offset
                    maxWidth: 300,
                    theme: 'light-border',
                    zIndex: 50,
                })[0];
            },

            /**
             * Called when suggestion props update
             */
            onUpdate(props) {
                component?.updateProps(props);
                popup?.setProps({
                    getReferenceClientRect: () =>
                        props.clientRect?.() ?? DOM_RECT_FALLBACK,
                });
            },

            /**
             * Handle keyboard events
             */
            onKeyDown(props) {
                if (props.event.key === 'Escape') {
                    popup?.hide();
                    return true;
                }

                if (!component?.ref) {
                    return false;
                }

                return component.ref.onKeyDown(props);
            },

            /**
             * Called when suggestion ends/closes
             */
            onExit() {
                callbacks?.onExit?.();
                popup?.destroy();
                component?.destroy();
                popup = undefined;
                component = undefined;
            },
        };
    },
});
